import React, { useMemo, useRef, useState } from 'react';
import { useEventListener, useLatest, useMountEffect, useSyncOutside } from 'shared/hooks';
import { Box } from '@mui/material';
import { buildFrame } from './frameRuntime';
import { useReportAssets } from 'features/report-files/reportAssets';
import { requestPresentationExit } from 'shared/ui/presentationMode';
import { readLiveChanges } from './api';
import { assetsCompatible, FrameAssets, pageSourceKey, resolvePatches, structureKey } from './liveFrame';
import { FrameFeedback, LiveAnswer, Page, Results, Selection, Interaction, QueryWindow, Result } from './types';
import { readSaveMessage, SaveError, SaveHandler, saveErrorOf, useSaveNotice } from './pageSave';

type Props = { reportId: string; assetRefreshKey?: number; page: Page; revision: number; results: Results; editing: boolean; ready: boolean; selection: Selection | null; onSelect: (s: Selection | null) => void;
  inspecting?: boolean; interaction?: Interaction | null; contextKey?: string;
  loadingSlots?: string[]; onClearInteraction?: () => void;
  onFeedback: (f: FrameFeedback) => void; onNavigate: (id: string) => void; onInteraction: (value: Interaction) => void;
  onQuery: (visualId: string, dataId: string, options: QueryWindow) => Promise<Result>;
  /** Live viewing: the net change from a version to the head. Without it every change rebuilds the frame (the screenshot host has no credentials). */
  liveChanges?: (pageUid: string, after: number, signal: AbortSignal) => Promise<LiveAnswer>;
  /** Page-script saves: true when this person may save text through the page's scripts (Edit content or above, signed in). */
  canSave?: boolean;
  /** Saves a page script's text on the server. Absent where saving is unavailable (public links, screenshots). */
  onSave?: SaveHandler;
  /** A public link: a save attempt is refused with the public-link message. */
  publicLink?: boolean };
export default function PageFrame(props: Props) {
  const assets = useReportAssets(props.reportId, pageSourceKey(props.page), props.assetRefreshKey ?? 0, props.page.id);
  // A page is its own stage: opening another page starts a new document, never a transition between the two.
  return <PageRenderer key={props.page.id} {...props} liveChanges={readLiveChanges} assets={assets} />;
}

/**
 * One document the page was built into. Its content and version are the **render base**: they change only when the
 * frame is rebuilt, or when the server's patches were applied to it in place (then the base advances without a remount).
 * The page the client currently knows (`props.page`, `props.revision`) can be ahead of it for a moment.
 */
type Generation = { id: number; token: string; page: Page; revision: number; sourceKey: string; assets: FrameAssets; canSave: boolean; source: string };
type Stage = { shown: Generation | null; incoming: Generation | null };
type Current = { page: Page; revision: number; sourceKey: string; assets: FrameAssets; liveChanges?: Props['liveChanges']; canSave?: boolean };
type Waiting = { gen: number; requestId: number; types: string[]; resolve: (data: any) => void };
let generations = 0;
const REBUILD_WAIT_MS = 3000;
const REPLY_WAIT_MS = 3000;
const SCROLL_WAIT_MS = 400;

const generation = (current: Current): Generation => {
  const token = crypto.randomUUID();
  const canSave = Boolean(current.canSave);
  return { id: ++generations, token, page: current.page, revision: current.revision, sourceKey: current.sourceKey, assets: current.assets, canSave,
    source: buildFrame(current.page, token, current.assets.urls, current.assets.errors, canSave) };
};

// Both the editor and isolated screenshot host use this exact renderer.
export function PageRenderer(props: Props & { assets: FrameAssets }) {
  const { page, revision, assets } = props;
  const sourceKey = useMemo(() => pageSourceKey(page), [page]);
  const current = useLatest<Current>({ page, revision, sourceKey, assets, liveChanges: props.liveChanges, canSave: props.canSave });
  const notice = useSaveNotice();
  const latest = useLatest(props);
  const [stage, setStage] = useState<Stage>(() => ({ shown: assets.ready ? generation({ page, revision, sourceKey, assets, canSave: props.canSave }) : null, incoming: null }));
  const staged = useRef(stage);
  const commit = (next: Stage) => { staged.current = next; setStage(next); };
  const frames = useRef(new Map<number, HTMLIFrameElement>());
  const lifecycle = useRef(new Map<number, { ready: boolean; settled: boolean }>());
  const waiting = useRef(new Set<Waiting>());
  const wakers = useRef(new Set<() => void>());
  const running = useRef(false);
  const again = useRef(false);
  const requests = useRef(0);
  const disposed = useRef(false);
  const aborts = useRef(new Set<AbortController>());
  useMountEffect(() => {
    // Development mounts, unmounts and mounts again: the stage is live again after the first cleanup.
    disposed.current = false;
    return () => {
      disposed.current = true;
      aborts.current.forEach(controller => controller.abort());
      waiting.current.forEach(entry => entry.resolve(null));
      wakers.current.forEach(wake => wake());
    };
  });

  // A frame (visible or still hidden) reports in: ready, first data rendered, or an answer to a question the stage asked it.
  const onLifecycle = (id: number, field: 'ready' | 'settled') => {
    const entry = lifecycle.current.get(id) ?? { ready: false, settled: false };
    entry[field] = true;
    lifecycle.current.set(id, entry);
    wakers.current.forEach(wake => wake());
  };
  const onReply = (id: number, data: any) => {
    waiting.current.forEach(entry => {
      if (entry.gen !== id || !entry.types.includes(data.type) || (data.requestId != null && data.requestId !== entry.requestId)) return;
      waiting.current.delete(entry);
      entry.resolve(data);
    });
  };
  const ask = (gen: Generation, message: object, types: string[], ms: number) => new Promise<any>(resolve => {
    const target = frames.current.get(gen.id)?.contentWindow;
    if (!target) { resolve(null); return; }
    const entry: Waiting = { gen: gen.id, requestId: ++requests.current, types, resolve: data => { clearTimeout(timer); resolve(data); } };
    const timer = setTimeout(() => { waiting.current.delete(entry); resolve(null); }, ms);
    waiting.current.add(entry);
    target.postMessage({ channel: 'unity-html', token: gen.token, requestId: entry.requestId, ...message }, '*');
  });
  /** Resolves when `done()` holds, the wait runs out, or the current page moved on (the caller then looks again). */
  const until = (done: () => boolean, ms: number) => new Promise<void>(resolve => {
    const finish = () => { clearTimeout(timer); wakers.current.delete(check); resolve(); };
    const check = () => { if (disposed.current || done()) finish(); };
    const timer = setTimeout(finish, ms);
    wakers.current.add(check);
    check();
  });

  /** Apply the server's net patches to the running document. */
  const tryLive = async (shown: Generation, now: Current): Promise<'applied' | 'wait' | 'rebuild'> => {
    if (!now.liveChanges || now.revision <= shown.revision || structureKey(shown.page) !== structureKey(now.page)
        || !assetsCompatible(shown.assets, now.assets)) return 'rebuild';
    const controller = new AbortController();
    aborts.current.add(controller);
    let answer: LiveAnswer;
    try { answer = await now.liveChanges(now.page.id, shown.revision, controller.signal); }
    catch { return 'rebuild'; }
    finally { aborts.current.delete(controller); }
    if (disposed.current) return 'wait';
    if (answer.rebuild || answer.version < now.revision) return 'rebuild';
    // The page changed again after this client read it: the workspace is fetching that newer content; show it then.
    if (answer.version > now.revision) return 'wait';
    const patches = resolvePatches(answer.patches, now.assets.urls);
    if (!patches) return 'rebuild';
    const reply = await ask(shown, { type: 'apply', fromVersion: shown.revision, version: answer.version, patches }, ['applied', 'apply-failed'], REPLY_WAIT_MS);
    if (reply?.type !== 'applied' || staged.current.shown?.id !== shown.id) return 'rebuild';
    // The render base advances without a remount; the revision change tells the frame's feedback which version it shows.
    commit({ shown: { ...shown, page: now.page, revision: now.revision, sourceKey: now.sourceKey, assets: now.assets }, incoming: null });
    return 'applied';
  };

  /** Build the new document in a hidden second frame, then swap: the old one stays visible until the new one has data. */
  const rebuild = async (shown: Generation, now: Current): Promise<boolean> => {
    const next = generation(now);
    commit({ shown, incoming: next });
    const position = ask(shown, { type: 'scroll-request' }, ['scroll'], SCROLL_WAIT_MS);
    // A newer page makes this build pointless: drop it and let the caller build the newer one.
    const stale = () => current.current.sourceKey !== next.sourceKey || !assetsCompatible(next.assets, current.current.assets) || next.canSave !== Boolean(current.current.canSave);
    await until(() => stale() || (lifecycle.current.get(next.id)?.ready === true && lifecycle.current.get(next.id)?.settled === true), REBUILD_WAIT_MS);
    if (disposed.current) return false;
    if (stale()) { commit({ shown, incoming: null }); lifecycle.current.delete(next.id); return false; }
    const scroll = await position;
    if (scroll && (scroll.x || scroll.y) && lifecycle.current.get(next.id)?.ready) {
      await ask(next, { type: 'scroll-to', x: scroll.x, y: scroll.y }, ['scrolled'], SCROLL_WAIT_MS);
    }
    commit({ shown: next, incoming: null });
    lifecycle.current.delete(shown.id);
    return true;
  };

  /** Make the visible document show the page the client knows: nothing, an in-place update, or a double-buffered rebuild. */
  const converge = async () => {
    for (let round = 0; round < 12 && !disposed.current; round++) {
      const now = current.current;
      if (!now.assets.ready) return;
      const { shown } = staged.current;
      if (!shown) { commit({ shown: generation(now), incoming: null }); return; }
      // A change of what the person may save rebuilds the document: its script then reads `unity.canSave()` afresh (fields enable or disable).
      if (shown.sourceKey === now.sourceKey && assetsCompatible(shown.assets, now.assets) && shown.canSave === Boolean(now.canSave)) {
        if (shown.revision !== now.revision) commit({ shown: { ...shown, revision: now.revision, page: now.page, assets: now.assets }, incoming: null });
        return;
      }
      const outcome = await tryLive(shown, now);
      if (outcome === 'wait') return;
      if (outcome === 'rebuild') await rebuild(shown, now);
    }
  };
  const kick = () => {
    wakers.current.forEach(wake => wake());
    if (running.current) { again.current = true; return; }
    running.current = true;
    void converge().catch(error => console.error('[html-live] the page could not be brought up to date', error)).finally(() => {
      running.current = false;
      if (again.current && !disposed.current) { again.current = false; kick(); }
    });
  };
  // kick and converge read the latest page through refs; the page, its version and its assets are what trigger them.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useSyncOutside(kick, [sourceKey, revision, assets.ready, assets.urls, assets.errors, props.canSave]);

  useSyncOutside(() => {
    if (!assets.ready) latest.current.onFeedback({revision: props.revision, errors: [], rendered: [], dataStatus: 'loading'});
  }, [assets.ready, props.revision, latest]);

  const frameFor = (gen: Generation) => <FrameDocument key={gen.id} {...props} gen={gen} visible={gen === stage.shown}
    register={(node) => { if (node) frames.current.set(gen.id, node); else frames.current.delete(gen.id); }}
    onLifecycle={onLifecycle} onReply={onReply} onSaveRefused={notice.show} />;
  return <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
    {/* One keyed list: the hidden frame that becomes the visible one keeps its element (no reload) when the old one goes. */}
    {stage.shown ? [stage.shown, ...(stage.incoming ? [stage.incoming] : [])].map(frameFor) :
      <Box role="status" sx={{p:3,color:'text.secondary'}}>Loading report images…</Box>}
    {notice.element}
  </Box>;
}

type FrameProps = Props & { gen: Generation; visible: boolean; register: (node: HTMLIFrameElement | null) => void;
  onLifecycle: (id: number, field: 'ready' | 'settled') => void; onReply: (id: number, data: any) => void; onSaveRefused: (error: SaveError) => void };

/** One iframe: the document of one generation and the message protocol with it. A hidden one only builds; the visible one is the page. */
function FrameDocument(props: FrameProps) {
  const { gen, visible, results, editing } = props;
  const token = gen.token;
  const frame = useRef<HTMLIFrameElement>(null);
  const latest = useRef(props);
  latest.current = props;
  const [readyToken, setReadyToken] = useState<string | null>(null);
  const sent = useRef<{token: string; results: Results}>();
  // Readiness belongs to one document, not the iframe element.
  const loaded = readyToken === token;
  const answerSave = (requestId: number, payload: object) =>
    frame.current?.contentWindow?.postMessage({ channel: 'unity-html', token, type: 'save-result', requestId, ...payload }, '*');
  /** A frame's save request is an untrusted hint: validate it, ask the server, tell the frame and (on refusal) the person. */
  const saveFromFrame = (data: unknown) => {
    const message = readSaveMessage(data);
    if (!message) return;
    const refuse = (error: SaveError) => {
      latest.current.onSaveRefused(error);
      answerSave(message.requestId, { ok: false, code: error.code, message: error.message });
    };
    if (message.error || !message.request) { if (message.error) refuse(message.error); return; }
    const { onSave, publicLink } = latest.current;
    if (!onSave) { refuse(new SaveError(publicLink ? 'public_link' : 'no_access')); return; }
    onSave(message.request).then(result => answerSave(message.requestId, { ok: true, version: result.version }), error => refuse(saveErrorOf(error)));
  };
  const receive = (event: MessageEvent) => {
    if (event.source !== frame.current?.contentWindow || event.data?.channel !== 'unity-html' || event.data?.token !== token) return;
    const { type, selection } = event.data;
    if (type === 'ready') { setReadyToken(token); props.onLifecycle(gen.id, 'ready'); }
    if (['scroll', 'scrolled', 'applied', 'apply-failed'].includes(type)) props.onReply(gen.id, event.data);
    if (type === 'query') {
      const { visualId, dataId, options, requestId } = event.data;
      const visual = latest.current.page.visuals.find(v => v.id === visualId);
      if (!visual?.dataIds.includes(dataId)) return;
      const key = latest.current.contextKey;
      const reply = (payload: object) => {
        if (key !== latest.current.contextKey) return;
        frame.current?.contentWindow?.postMessage({channel:'unity-html',token,type:'query-result',requestId,...payload},'*');
      };
      latest.current.onQuery(visualId,dataId,options || {}).then(result=>reply({result})).catch(error=>reply({error:error.message}));
    }
    if (type === 'feedback' && Array.isArray(event.data.errors) && event.data.dataStatus === 'ready') props.onLifecycle(gen.id, 'settled');
    // A hidden document is only being built: nothing it reports is about the page the person is looking at.
    if (!latest.current.visible) return;
    if (type === 'selection') {
      if (!(latest.current.inspecting ?? latest.current.editing)) return;
      if (selection === null) { latest.current.onSelect(null); return; }
      if (!['visual', 'data'].includes(selection?.kind)) return;
      const known = selection?.kind === 'visual' ? latest.current.page.visuals : latest.current.page.data;
      if (!known.some(item => item.id === selection?.id)) return;
      if (selection.selectedVisualIds != null && (!Array.isArray(selection.selectedVisualIds) || selection.kind !== 'visual'
          || !selection.selectedVisualIds.every((id: unknown) => latest.current.page.visuals.some(v => v.id === id)))) return;
      latest.current.onSelect(selection);
    }
    if (type === 'feedback' && Array.isArray(event.data.errors) && (event.data.revision == null || event.data.revision === latest.current.gen.revision)
        && (!latest.current.contextKey || event.data.contextKey === latest.current.contextKey))
      latest.current.onFeedback({ errors: event.data.errors.slice(0, 30), rendered: event.data.rendered || [], layout: event.data.layout,
        revision: event.data.revision ?? latest.current.gen.revision, contextKey: event.data.contextKey, warnings: event.data.warnings, theme: event.data.theme, bindings: event.data.bindings, dataStatus: event.data.dataStatus });
    if (type === 'save') saveFromFrame(event.data);
    if (type === 'navigate' && typeof event.data.pageId === 'string') latest.current.onNavigate(event.data.pageId);
    if (type === 'escape') requestPresentationExit();
    if (type === 'interaction') latest.current.onInteraction(event.data.interaction);
    if (type === 'clear-interaction') latest.current.onClearInteraction?.();
  };
  useEventListener('message', receive);
  useSyncOutside(() => {
    if (!loaded || !props.ready) return;
    const previous = sent.current?.token === token ? sent.current.results : undefined;
    const changedSlots = previous ? Array.from(new Set([...Object.keys(previous), ...Object.keys(results)]))
      .filter(key => previous[key] !== results[key]) : undefined;
    frame.current?.contentWindow?.postMessage({ channel: 'unity-html', token, type: 'data',
      results, changedSlots, editing,
      revision: gen.revision, contextKey: props.contextKey, inspecting: props.inspecting, interaction: props.interaction }, '*');
    sent.current = {token, results};
  }, [loaded, props.ready, results, token, props.contextKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadingKey = JSON.stringify(props.loadingSlots || []);
  useSyncOutside(() => {
    if(loaded) frame.current?.contentWindow?.postMessage({channel:'unity-html',token,type:'loading',slots:JSON.parse(loadingKey)},'*');
  },[loaded,loadingKey,token]);
  useSyncOutside(() => {
    if(loaded) frame.current?.contentWindow?.postMessage({channel:'unity-html',token,type:'interaction-state',interaction:props.interaction},'*');
  },[loaded,props.interaction,token]);
  // The frame stamps its feedback with the version it shows: the render base, which an applied patch or a swap advances.
  // A document that just became visible reports again (it built hidden, and an unchanged report is not re-sent).
  useSyncOutside(() => {
    if (loaded && props.ready && visible) frame.current?.contentWindow?.postMessage({ channel: 'unity-html', token, type: 'inspect', revision: gen.revision }, '*');
  }, [loaded, props.ready, gen.revision, token, visible]);
  useSyncOutside(() => {
    frame.current?.contentWindow?.postMessage({ channel: 'unity-html', token, type: 'mode', editing, inspecting: props.inspecting }, '*');
  }, [editing, props.inspecting, token]);
  useSyncOutside(() => {
    if (loaded) frame.current?.contentWindow?.postMessage({ channel: 'unity-html', token, type: 'selection', selection: props.selection }, '*');
  }, [loaded, props.selection, token]);
  return <iframe ref={node => { (frame as React.MutableRefObject<HTMLIFrameElement | null>).current = node; props.register(node); }}
    title={gen.page.name} sandbox="allow-scripts allow-forms" srcDoc={gen.source} aria-hidden={visible ? undefined : true} tabIndex={visible ? undefined : -1}
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block',
      visibility: visible ? 'visible' : 'hidden', pointerEvents: visible ? 'auto' : 'none' }} />;
}
